// Gallery.jsx - Galería integrada con API
import React, { useState, useEffect } from 'react';
import { FaHeart, FaPalette, FaSmile, FaGamepad, FaLeaf, FaExpand, FaTimes, FaChevronLeft, FaChevronRight, FaImages, FaPlay, FaVideo, FaSpinner } from 'react-icons/fa';
import api from '../../services/api';

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('todas');
  const [images, setImages] = useState({});
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showVideos, setShowVideos] = useState(false);
  const [error, setError] = useState(null);

  // Cargar datos al montar
  useEffect(() => {
    loadGalleryData();
  }, []);

  // Verificar localStorage para mostrar videos
  useEffect(() => {
    const shouldShowVideos = localStorage.getItem('showVideos');
    if (shouldShowVideos === 'true') {
      setShowVideos(true);
      localStorage.removeItem('showVideos');
    }
  }, []);

  const loadGalleryData = async () => {
    try {
      setLoading(true);

      const [galleryResponse, videosResponse, categoriesResponse] = await Promise.all([
        api.getGallery(),
        api.getVideos(),
        api.getCategories('gallery')
      ]);

      // Procesar categorías primero
      const categoriesData = categoriesResponse?.data?.items || categoriesResponse?.data || categoriesResponse || [];
      const categoriesArray = Array.isArray(categoriesData) ? categoriesData : [];

      console.log('Categorías recibidas:', categoriesArray);

      // Procesar imágenes - estructura: data.categoria.images
      const galleryData = galleryResponse?.data || galleryResponse || {};
      const imagesGrouped = {};

      console.log('Datos de galería recibidos:', galleryData);

      // Extraer imágenes de cada categoría usando slug
      Object.keys(galleryData).forEach(categorySlug => {
        const categoryData = galleryData[categorySlug];
        if (categoryData && categoryData.images) {
          // Encontrar categoría por slug
          const category = categoriesArray.find(c => c.slug === categorySlug);
          const categoryId = category?.id || categorySlug;

          console.log(`Procesando categoría ${categorySlug}, ID: ${categoryId}, imágenes: ${categoryData.images.length}`);

          imagesGrouped[categoryId] = categoryData.images.map(img => ({
            ...img,
            image_url: img.url,
            category_id: categoryId
          }));
        }
      });

      // Agregar categoría "todas" con todas las imágenes
      const allImages = Object.values(imagesGrouped).flat();
      imagesGrouped.todas = allImages;

      console.log('Imágenes agrupadas finales:', imagesGrouped);
      console.log('Total de imágenes:', allImages.length);

      setImages(imagesGrouped);

      // Procesar videos
      const videosData = videosResponse?.data?.items || videosResponse?.items || videosResponse?.data || videosResponse || [];
      setVideos(Array.isArray(videosData) ? videosData : []);

      // Mapear categorías usando el array ya procesado
      const mappedCategories = [
        {
          id: 'todas',
          name: 'Todas',
          slug: 'todas',
          icon: 'FaImages',
          color: 'from-purple-400 to-pink-400'
        },
        ...categoriesArray.map(cat => ({
          ...cat,
          icon: getIconForCategory(cat.slug),
          color: cat.color || getColorForCategory(cat.slug)
        }))
      ];

      setCategories(mappedCategories);

    } catch (err) {
      console.error('Error cargando galería:', err);
      setError('No se pudo cargar la galería');
    } finally {
      setLoading(false);
    }
  };

  // Agrupar imágenes por categoría si vienen como array plano
  const groupImagesByCategory = (imagesArray) => {
    const grouped = {};
    imagesArray.forEach(img => {
      const catId = img.category_id || 'sin-categoria';
      if (!grouped[catId]) grouped[catId] = [];
      grouped[catId].push(img);
    });
    return grouped;
  };

  // Mapear iconos según slug de categoría
  const getIconForCategory = (slug) => {
    const iconMap = {
      'empatia': 'FaHeart',
      'arte': 'FaPalette',
      'emociones': 'FaSmile',
      'actividades': 'FaGamepad',
      'naturaleza': 'FaLeaf'
    };
    return iconMap[slug] || 'FaImages';
  };

  // Mapear colores según slug
  const getColorForCategory = (slug) => {
    const colorMap = {
      'empatia': 'from-pink-400 to-red-400',
      'arte': 'from-blue-400 to-purple-400',
      'emociones': 'from-yellow-400 to-orange-400',
      'actividades': 'from-green-400 to-blue-400',
      'naturaleza': 'from-green-400 to-teal-400'
    };
    return colorMap[slug] || 'from-gray-400 to-gray-500';
  };

  // Obtener componente de icono
  const getIconComponent = (iconName) => {
    const icons = {
      FaImages,
      FaHeart,
      FaPalette,
      FaSmile,
      FaGamepad,
      FaLeaf
    };
    return icons[iconName] || FaImages;
  };

  // Obtener contenido filtrado
  const getFilteredContent = () => {
    if (showVideos) {
      // Filtrar videos por categoría
      if (selectedCategory === 'todas') {
        return videos;
      }
      return videos.filter(v => v.category_id === selectedCategory);
    } else {
      // Obtener imágenes de la categoría seleccionada
      return images[selectedCategory] || [];
    }
  };

  // Contar contenido por categoría
  const getContentCount = (categoryId) => {
    if (showVideos) {
      if (categoryId === 'todas') return videos.length;
      return videos.filter(v => v.category_id === categoryId).length;
    } else {
      return (images[categoryId] || []).length;
    }
  };

  // Abrir modal
  const openModal = (content, index) => {
    if (content.video_url || content.type === 'video' || showVideos) {
      setSelectedVideo(content);
      setSelectedImage(null);
    } else {
      setSelectedImage(content);
      setSelectedVideo(null);
    }
    setCurrentIndex(index);
    document.body.style.overflow = 'hidden';
  };

  // Cerrar modal
  const closeModal = () => {
    setSelectedImage(null);
    setSelectedVideo(null);
    setCurrentIndex(0);
    document.body.style.overflow = 'unset';
  };

  // Navegación en modal
  const navigateContent = (direction) => {
    const filteredContent = getFilteredContent();
    if (filteredContent.length === 0) return;

    let newIndex = currentIndex + direction;
    if (newIndex < 0) newIndex = filteredContent.length - 1;
    if (newIndex >= filteredContent.length) newIndex = 0;

    setCurrentIndex(newIndex);
    const newContent = filteredContent[newIndex];

    if (newContent.video_url || newContent.type === 'video' || showVideos) {
      setSelectedVideo(newContent);
      setSelectedImage(null);
    } else {
      setSelectedImage(newContent);
      setSelectedVideo(null);
    }
  };

  const filteredContent = getFilteredContent();

  // Estado de carga
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="text-6xl text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-purple-700 font-medium">Cargando galería...</p>
        </div>
      </div>
    );
  }

  // Estado de error
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <div className="text-center bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl max-w-md">
          <FaImages className="text-6xl text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-700 mb-2">Error</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadGalleryData}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-full font-bold hover:shadow-lg transition-all"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 py-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-white/80 rounded-full px-6 py-3 backdrop-blur-sm mb-6">
            <FaImages className="text-purple-600 text-xl" />
            <span className="font-medium text-gray-700">Galería FeelLab</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-titulo font-bold text-gray-800 mb-4">
            Nuestros
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {' '}Momentos Mágicos
            </span>
          </h1>

          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Descubre las experiencias únicas que vivimos en el Laboratorio de Empatía Animal,
            donde cada momento es una oportunidad para conectar con las emociones y la naturaleza.
          </p>
        </div>

        {/* Toggle entre fotos y videos */}
        <div className="flex justify-center mb-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-full p-2 shadow-lg border border-gray-200">
            <button
              onClick={() => setShowVideos(false)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 flex items-center space-x-2 ${!showVideos
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md'
                  : 'text-gray-600 hover:text-blue-500 hover:bg-blue-50'
                }`}
            >
              <FaImages />
              <span>Fotos</span>
            </button>
            <button
              onClick={() => setShowVideos(true)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 flex items-center space-x-2 ${showVideos
                  ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-md'
                  : 'text-gray-600 hover:text-red-500 hover:bg-red-50'
                }`}
            >
              <FaVideo />
              <span>Videos</span>
            </button>
          </div>
        </div>

        {/* Filtros de categorías */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => {
            const IconComponent = getIconComponent(category.icon);
            const isActive = selectedCategory === category.id;
            const contentCount = getContentCount(category.id);

            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`group flex items-center space-x-3 px-6 py-3 rounded-full font-medium transition-all duration-300 transform hover:scale-105 ${isActive
                    ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
                  }`}
              >
                <IconComponent className={`text-lg ${isActive ? 'text-white' : 'text-gray-500'}`} />
                <span>{category.name}</span>
                <span className={`text-sm px-2 py-1 rounded-full ${isActive ? 'bg-white/30' : 'bg-gray-200'
                  }`}>
                  {contentCount}
                </span>
              </button>
            );
          })}
        </div>

        {/* Grid de contenido */}
        {filteredContent.length === 0 ? (
          <div className="text-center py-16 bg-white/50 rounded-3xl">
            {showVideos ? (
              <FaVideo className="text-gray-300 text-6xl mx-auto mb-4" />
            ) : (
              <FaImages className="text-gray-300 text-6xl mx-auto mb-4" />
            )}
            <h3 className="text-xl font-bold text-gray-500 mb-2">
              No hay {showVideos ? 'videos' : 'imágenes'} disponibles
            </h3>
            <p className="text-gray-400">
              {selectedCategory === 'todas'
                ? 'Aún no se ha agregado contenido a la galería'
                : 'No hay contenido en esta categoría'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredContent.map((content, index) => (
              <div
                key={content.id}
                className="group relative aspect-square bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 cursor-pointer"
                onClick={() => openModal(content, index)}
              >
                {(content.video_url || showVideos) ? (
                  <div className="relative w-full h-full">
                    {content.thumbnail_url ? (
                      <img
                        src={content.thumbnail_url}
                        alt={content.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-red-100 to-pink-100 flex items-center justify-center">
                        <FaVideo className="text-red-400 text-4xl" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <FaPlay className="text-white text-3xl drop-shadow-lg" />
                    </div>
                  </div>
                ) : (
                  <img
                    src={content.image_url}
                    alt={content.title || content.description}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}

                {/* Overlay con efecto hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-medium text-sm mb-1">
                      {content.title || content.description || 'Sin título'}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-white/80 text-xs capitalize">
                        {categories.find(c => c.id === content.category_id)?.name || 'General'}
                      </span>
                      <FaExpand className="text-white text-sm" />
                    </div>
                  </div>
                </div>

                {/* Indicador de categoría */}
                <div className="absolute top-3 right-3">
                  <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${categories.find(c => c.id === content.category_id)?.color || 'from-gray-400 to-gray-500'
                    }`}></div>
                </div>

                {/* Indicador de tipo */}
                <div className="absolute top-3 left-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1">
                  {(content.video_url || showVideos) ? <FaVideo /> : <FaImages />}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de imagen */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative max-w-5xl w-full">
            {/* Botones de navegación */}
            <button
              onClick={() => navigateContent(-1)}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all z-10"
            >
              <FaChevronLeft className="text-xl" />
            </button>

            <button
              onClick={() => navigateContent(1)}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all z-10"
            >
              <FaChevronRight className="text-xl" />
            </button>

            {/* Botón cerrar */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all z-10"
            >
              <FaTimes className="text-xl" />
            </button>

            {/* Imagen */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title}
                className="w-full max-h-[70vh] object-contain"
              />
              {(selectedImage.title || selectedImage.description) && (
                <div className="p-6">
                  {selectedImage.title && (
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{selectedImage.title}</h3>
                  )}
                  {selectedImage.description && (
                    <p className="text-gray-600">{selectedImage.description}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de video */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative max-w-5xl w-full">
            {/* Botones de navegación */}
            <button
              onClick={() => navigateContent(-1)}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all z-10"
            >
              <FaChevronLeft className="text-xl" />
            </button>

            <button
              onClick={() => navigateContent(1)}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all z-10"
            >
              <FaChevronRight className="text-xl" />
            </button>

            {/* Botón cerrar */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg transition-all z-10"
            >
              <FaTimes className="text-xl" />
            </button>

            {/* Video */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
              <div className="aspect-video">
                <iframe
                  src={selectedVideo.embed_url}
                  className="w-full h-full"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  title={selectedVideo.title}
                />
              </div>
              {(selectedVideo.title || selectedVideo.description) && (
                <div className="p-6">
                  {selectedVideo.title && (
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{selectedVideo.title}</h3>
                  )}
                  {selectedVideo.description && (
                    <p className="text-gray-600">{selectedVideo.description}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
